import React, { useEffect, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import TextField from "@mui/material/TextField";
import AddIcon from '@mui/icons-material/Add';
import { Autocomplete, CircularProgress, IconButton, styled, Typography } from '@mui/material';
import useSWR from 'swr';
import Keys from '../../../../services/keys';
import { FormsClient } from '../../../../api/web-api-client';
import clientService from '../../../../services/clientService';
import { useDebouncedCallback } from 'use-debounce';
import IComponent from '../../../../services/forms.interface';
import { IMedicationVm } from '../interfaces';
import { useAppContext } from '../../../../context/app-context';

interface ICustomProps {
    name: string;
    type: string;
    value: string;
    defaultValue: string;
    disabled: boolean;
    read_only: string;
    required: boolean;
    maskKey: string;
    error: any;
    onChange: any;
    onBlur: any;
    label: string;
}

interface IAllergyProps {
    parentId: string;
    localProps: ICustomProps;
    count: number;
    maxRows: number;
    index: number;
    components: IComponent[];
    isMobile?: boolean;
    disabled: boolean;
    handleChange: (event: any, id: string, value: string, parentId: string) => void;
    medications: string[];
}

const useStyles = makeStyles((theme) => ({
    allergiesContainer: {
        display: 'flex',
        flexDirection: 'column',
        flexBasis: '100%'
    },
    allergy: {
        display: 'flex',
        flexDirection: 'row',
        flexBasis: '100%',
        margin: '5px'
    },
    addIcon: {
        width: '30px',
    },
    addDiv: {
        flexBasis: '30px',
        minWidth: '30px',
        jusifyContent: 'flex-end'
    },
    textFieldName: {
        flexBasis: '100%',
        marginRight: '0px',
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        flexBasis: '100%',
        marginTop: '10px',
        flexWrap: (props: any) => props.isMobile ? 'wrap' : 'nowrap',
    },
    column1: {
        display: 'flex',
        flexGrow: '1',
        flexDirection: 'row',
        flexBasis: (props: any) => props.isMobile ? '100%' : '30%',
    },
    column2: {
        display: 'flex',
        flexGrow: '1',
        flexDirection: 'row',
        flexBasis: (props: any) => props.isMobile ? '100%' : '68%',
        flexWrap: (props: any) => props.isMobile ? 'wrap' : 'nowrap',
        justifyContent: 'flex-end'
    }
}));


const StyledAutocomplete = styled(Autocomplete)({
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#F9F9F9',
    },
});



const Allergy: React.FC<IAllergyProps> = props => {
    const [vm, setVm] = useState({ inputValue: '', searchError: false } as IMedicationVm);
    const classes = useStyles({ isMobile: props.isMobile });
    const { data, isValidating } = useSWR([Keys.ApiMedications, vm.inputValue], (url, search: string) => medicationsFetcher(search).then(res => res));
    const { unwrapperedDomain } = useAppContext();

    const medicationsFetcher = async (searchText: string) => {
        if (!searchText)
            return null;
        try {
            const client = clientService.getFormsClient(FormsClient, { apiPath: unwrapperedDomain });
            const result = await client.listMedications(searchText);
            result.sort((a, b) => (a.toLowerCase() > b.toLowerCase()) ? 1 : -1)
            return result;
        } catch (e) {
        }
    }

    const handleNameChange = (event, value) => {
        if (value)
            props.handleChange(event, event.target.id.substring(0, 36), value, props.parentId);
    };

    const handleChange = (event) => {
        props.handleChange(event, event.target.id, event.target.value, props.parentId);
    };

    const handleClear = (event) => {
        props.handleChange(event, props.components[0].id, null, props.parentId);
    };

    const debouncedInput = useDebouncedCallback(
        (value) => {
            if (value.length <= 2 && value.length > 0) {
                setVm({ ...vm, searchError: true });
            } else {
                setVm({ ...vm, inputValue: value, searchError: false });
            }
        },
        800
    );
    
    return (
        <div className={classes.row}>
            <div className={classes.column1}>
                <Typography variant="subtitle1" fontSize={17}>
                    {'Allergy #' + (+props.index + 1)}
                </Typography></div>
            <div className={classes.column2}>
                <StyledAutocomplete
                    id={props.components[0].id}
                    sx={{ width: 200, mr: 1 }}
                    onChange={(event, value) => handleNameChange(event, value)}
                    value={props.components[0].value || ''}
                    renderInput={(params) => (
                        <TextField {...params} label={vm.searchError ? 'Min. 3 characters' : 'Select Allergy'} fullWidth variant="outlined"
                            {...props.localProps} name={props.components[0].name} className={classes.textFieldName} size="small"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {isValidating ? <CircularProgress size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            error={vm.searchError}
                        />
                    )}
                    options={!data || isValidating ? [] : data}
                    filterOptions={(x) => x}
                    autoComplete
                    fullWidth
                    includeInputInList
                    filterSelectedOptions
                    className={classes.textFieldName}
                    freeSolo
                    onInputChange={(event, newInputValue) => {

                        if (event?.type === 'click' && !newInputValue)
                            handleClear(event);

                        // ignore blur (clicking off without selecting) and click events (clicking the list), those aren't someone typing
                        if (event?.type !== 'change')
                            return;
                        debouncedInput(newInputValue);
                        handleChange(event);
                    }}
                    onBlur={(elem) => {
                        setVm({ ...vm, searchError: false });
                        return;
                    }}
                />

            </div>
        </div>)
}

const Allergies: React.FC<IComponent> = props => {
    const classes = useStyles({ isMobile: props.isMobile });

    const maxRows = Object.keys(props.childComponents).length;

    const isRequired = (props.hasOwnProperty('required') && props.required === true && !props.read_only);

    const localProps: ICustomProps = {} as ICustomProps;
    localProps.type = 'text';

    localProps.required = isRequired;

    if (props.error) { localProps.error = true; }

    if (props.disabled)
        localProps.disabled = true;

    let baseClasses = 'item SortableItem rfb-item';
    if (props.editMode) { baseClasses += ' is-editing'; }
    if (!props.editMode) { baseClasses += ' not-is-editing'; }
    if (props.selected) { baseClasses += ' selected'; }


    // Find all filled problem components
    const filled = Object.values(props.childComponents).filter((comp: any) =>
        typeof comp?.name === 'string' &&
        comp.name.startsWith('health_history_allergies_') &&
        comp.value &&
        comp.value.trim() !== ''
    );

    // Always show at least one row, and allow adding up to maxRows
    const rowCount = Math.min(
        Math.max(filled.length + 1, 1),
        maxRows
    );

    const getComponents = (index) => {
        index++;
        const row = [];
        Object.values(props.childComponents).forEach((comp: any, i) => {
            if (comp?.name === 'health_history_allergies_antibiotic' + index + '_name' || comp?.name === 'health_history_allergies_' + index + '_name')
                row.push(comp);
        });
        return row;
    }

    const medications = props.medications?.length > 0 ? props.medications : [''];

    const list = [];

    for (let i = 0; i < rowCount; i++) {
        list.push(<Allergy key={i} parentId={props.id} localProps={localProps} count={rowCount} index={i} components={getComponents(i)} handleChange={props.handleChange} medications={medications} isMobile={props.isMobile} maxRows={maxRows} disabled={localProps.disabled} />)
    }

    return (
        <div className={baseClasses}>
            <div className={classes.allergiesContainer}>
                {list}
            </div>
        </div>
    );
}

export default Allergies;



